
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon } from "lucide-react";
import { format, parseISO, isValid } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { ProjectSubtask, SubtaskFormData } from '../types';
import { ResourceAssignment } from '../../resource-assignment/types';

// Helpers for date display (Singapore "dd MMM yyyy") with YYYY-MM-DD storage.
const parseYMD = (v: string): Date | null => {
  if (!v) return null;
  const d = /^\d{4}-\d{2}-\d{2}$/.test(v) ? parseISO(v) : new Date(v);
  return isValid(d) ? d : null;
};
const toYMDString = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};
const displayDate = (v: string): string => {
  const d = parseYMD(v);
  return d ? format(d, 'dd MMM yyyy') : 'Select date';
};

interface SubtaskFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (formData: SubtaskFormData) => Promise<void>;
  editingSubtask: ProjectSubtask | null;
  resourceAssignments: ResourceAssignment[];
  selectedTaskDetails?: any;
  existingSteps?: { step: number; step_name: string }[];
  existingSubtasks?: ProjectSubtask[];
  projectId?: string;
}

const SubtaskForm = ({ 
  isOpen, 
  onClose, 
  onSave, 
  editingSubtask, 
  resourceAssignments, 
  selectedTaskDetails,
  existingSteps = [],
  existingSubtasks = [],
  projectId
}: SubtaskFormProps) => {

  const [formData, setFormData] = useState<SubtaskFormData>({
    subtask_name: '',
    description: '',
    resource_id: '',
    status: 'To Do',
    estimated_hours: 8,
    actual_hours: undefined,
    start_date: '',
    end_date: '',
    step: 1,
    step_name: 'Step 1',
    email: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isDuplicateName, setIsDuplicateName] = useState(false);

  // Function to fetch email for a resource_id
  const fetchResourceEmail = async (resourceId: string) => {
    console.log('🔍 SUBTASK FORM: Fetching email for resource_id:', resourceId);
    
    try {
      const { data: resourceData, error: resourceError } = await supabase
        .from('resources')
        .select('email, name, role')
        .eq('id', resourceId)
        .single();

      console.log('🔍 SUBTASK FORM: Resources table query result:', {
        resourceData,
        resourceError,
        queryResourceId: resourceId
      });

      if (resourceError) {
        console.error('❌ SUBTASK FORM: Error fetching resource email:', resourceError);
        return null;
      } else if (resourceData?.email) {
        console.log('✅ SUBTASK FORM: Found email from resources table:', resourceData.email);
        return resourceData.email;
      } else {
        console.warn('⚠️ SUBTASK FORM: No email found in resources table for resource_id:', resourceId);
        return null;
      }
    } catch (error) {
      console.error('❌ SUBTASK FORM: Error querying resources table:', error);
      return null;
    }
  };

  // Check for duplicate subtask names. The locally-visible `existingSubtasks`
  // list does NOT include subtasks created by other Project Managers or
  // Directors (RLS). To prevent cross-creator duplicates we ALSO verify
  // against the full project_subtasks set via the elevated edge function
  // (service role bypasses RLS), debounced on input.
  const duplicateCheckTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const duplicateCheckSeq = React.useRef(0);

  const checkDuplicateName = (name: string) => {
    if (!name.trim()) {
      setIsDuplicateName(false);
      return;
    }

    const trimmedName = name.trim().toLowerCase();
    const localDuplicate = existingSubtasks.some(subtask => {
      if (editingSubtask && subtask.id === editingSubtask.id) return false;
      return subtask.subtask_name.toLowerCase() === trimmedName;
    });

    if (localDuplicate) {
      setIsDuplicateName(true);
      return;
    }

    // Optimistically clear; remote check confirms.
    setIsDuplicateName(false);

    if (!projectId) return;
    if (duplicateCheckTimer.current) clearTimeout(duplicateCheckTimer.current);
    const seq = ++duplicateCheckSeq.current;
    duplicateCheckTimer.current = setTimeout(async () => {
      try {
        const { data, error } = await supabase.functions.invoke(
          'manage-subtask-elevated',
          {
            body: {
              action: 'check_duplicate_name',
              project_id: projectId,
              subtask_name: name.trim(),
              exclude_subtask_id: editingSubtask?.id ?? null,
            },
          }
        );
        if (seq !== duplicateCheckSeq.current) return;
        if (error) return;
        if ((data as any)?.duplicate === true) setIsDuplicateName(true);
      } catch {
        // Silent: local check still applies; UI just won't show remote
        // confirmation if the function is briefly unavailable.
      }
    }, 400);
  };


  useEffect(() => {
    console.log('🔍 SUBTASK FORM: useEffect triggered', {
      editingSubtask: !!editingSubtask,
      isOpen,
      resourceAssignmentsCount: resourceAssignments.length,
      existingSteps
    });

    const setupFormData = async () => {
      if (editingSubtask) {
        // Find the resource assignment that matches the resource_id
        const matchingAssignment = resourceAssignments.find(assignment => assignment.resource_id === editingSubtask.resource_id);
        
        console.log('🔍 SUBTASK FORM: Setting up edit mode', {
          subtaskResourceId: editingSubtask.resource_id,
          matchingAssignment: matchingAssignment,
          step: editingSubtask.step,
          stepName: editingSubtask.step_name,
          currentEmail: editingSubtask.email
        });

        let emailToUse = editingSubtask.email || '';
        
        // If no email exists, fetch it from resources table
        if (!emailToUse && editingSubtask.resource_id) {
          console.log('📧 SUBTASK FORM: No email in subtask, fetching from resources table for resource_id:', editingSubtask.resource_id);
          const fetchedEmail = await fetchResourceEmail(editingSubtask.resource_id);
          if (fetchedEmail) {
            emailToUse = fetchedEmail;
            console.log('✅ SUBTASK FORM: Successfully fetched email for existing subtask:', fetchedEmail);
          }
        }
        
        setFormData({
          subtask_name: editingSubtask.subtask_name,
          description: editingSubtask.description || '',
          // Fallback to original resource_id when no matching assignment exists,
          // so the Update button doesn't get stuck disabled.
          resource_id: matchingAssignment?.id || editingSubtask.resource_id || '',
          status: editingSubtask.status,
          estimated_hours: editingSubtask.estimated_hours || 8,
          actual_hours: editingSubtask.actual_hours || undefined,
          start_date: editingSubtask.start_date || '',
          end_date: editingSubtask.end_date || '',
          step: editingSubtask.step || 1,
          step_name: editingSubtask.step_name || 'Step 1',
          email: emailToUse
        });
      } else {
        // Auto-populate dates from selected task when creating new subtask.
        // Normalize to YYYY-MM-DD so the HTML <input type="date"> renders the value.
        const toYMD = (v: any): string => {
          if (!v) return '';
          if (typeof v === 'string') {
            if (/^\d{4}-\d{2}-\d{2}/.test(v)) return v.slice(0, 10);
            const d = new Date(v);
            if (!isNaN(d.getTime())) {
              const y = d.getFullYear();
              const m = String(d.getMonth() + 1).padStart(2, '0');
              const dd = String(d.getDate()).padStart(2, '0');
              return `${y}-${m}-${dd}`;
            }
            return '';
          }
          return '';
        };
        const taskStartDate = toYMD(selectedTaskDetails?.startDate);
        const taskEndDate = toYMD(selectedTaskDetails?.endDate);
        
        console.log('🔍 SUBTASK FORM: Setting up create mode', {
          taskStartDate,
          taskEndDate,
          existingSteps
        });
        
        setFormData({
          subtask_name: '',
          description: '',
          resource_id: '',
          status: 'To Do',
          estimated_hours: 8,
          actual_hours: undefined,
          start_date: taskStartDate,
          end_date: taskEndDate,
          step: 1,
          step_name: 'Step 1',
          email: ''
        });
      }
      
      // Reset duplicate name check
      setIsDuplicateName(false);
    };

    if (isOpen) {
      setupFormData();
    }
  }, [editingSubtask, isOpen, selectedTaskDetails, resourceAssignments, existingSteps]);

  const handleResourceChange = async (resourceAssignmentId: string) => {
    console.log('📋 SUBTASK FORM: Resource selected - Assignment ID:', resourceAssignmentId);

    if (!resourceAssignmentId) {
      console.warn('⚠️ SUBTASK FORM: Empty resource selection ignored');
      return;
    }

    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(resourceAssignmentId)) {
      console.warn('⚠️ SUBTASK FORM: Non-UUID resource selection ignored:', resourceAssignmentId);
      return;
    }

    const selectedAssignment = resourceAssignments.find(assignment => assignment.id === resourceAssignmentId);

    if (!selectedAssignment) {
      // The selected value is not an assignment id (e.g. the synthetic
      // current-resource entry shown when editing). Preserve it as the
      // resource_id without trying to derive name/role/email.
      console.warn('⚠️ SUBTASK FORM: No matching assignment for selection; preserving value as resource_id');
      setFormData(prev => ({ ...prev, resource_id: resourceAssignmentId }));
      return;
    }

    console.log('📋 SUBTASK FORM: Found assignment data:', {
      assignmentId: selectedAssignment.id,
      resourceId: selectedAssignment.resource_id,
      resourceName: selectedAssignment.name,
      resourceRole: selectedAssignment.role
    });

    // Auto-populate estimated hours and dates from resource allocation
    // Auto-populate estimated hours; preserve existing dates so new subtasks
    // continue to follow the project task's start/end dates unless the user
    // changes them manually.
    let estimatedHours = 8;
    let startDate = formData.start_date;
    let endDate = formData.end_date;
    let email = '';

    if (selectedAssignment.allocation_hrs) {
      estimatedHours = parseFloat(selectedAssignment.allocation_hrs) || 8;
    }

    // Get email from resources table using resource_id
    if (selectedAssignment.resource_id) {
      const fetchedEmail = await fetchResourceEmail(selectedAssignment.resource_id);
      if (fetchedEmail) {
        email = fetchedEmail;
      }
    }

    // Fallback to assignment email if available and no email from resources table
    if (!email && selectedAssignment.email) {
      email = selectedAssignment.email;
      console.log('📧 SUBTASK FORM: Using fallback email from assignment:', email);
    }

    console.log('📧 SUBTASK FORM: Final email value to be set:', email);
    
    setFormData(prev => ({
      ...prev,
      resource_id: resourceAssignmentId,
      estimated_hours: estimatedHours,
      start_date: startDate,
      end_date: endDate,
      email: email
    }));

    console.log('✅ SUBTASK FORM: Form data updated with email:', email);
  };

  const handleStepChange = (stepNumber: string) => {
    const step = parseInt(stepNumber);
    const stepInfo = existingSteps.find(s => s.step === step);
    
    setFormData(prev => ({
      ...prev,
      step: step,
      step_name: stepInfo?.step_name || `Step ${step}`
    }));
  };

  const handleSubtaskNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, subtask_name: value }));
    checkDuplicateName(value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('📋 SUBTASK FORM: Form submission attempted', { formData });
    
    if (!formData.subtask_name.trim()) {
      alert('Please enter a subtask name');
      return;
    }

    if (isDuplicateName) {
      alert('Please choose a unique subtask name');
      return;
    }

    if (!formData.resource_id) {
      alert('Please select an assigned resource');
      return;
    }

    // For new subtasks the resource_id must be a resource_assignment UUID.
    // For edits we accept any UUID (could be the underlying resource UUID).
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!editingSubtask && !uuidRegex.test(formData.resource_id)) {
      console.error('❌ SUBTASK FORM: Invalid assignment ID format in form:', formData.resource_id);
      alert(`Error: Invalid resource assignment ID format. Please select a resource again.`);
      return;
    }

    setIsLoading(true);
    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      console.error('❌ SUBTASK FORM: Error submitting form:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Sort resource assignments alphabetically by name
  const sortedResourceAssignments = [...resourceAssignments].sort((a, b) => {
    const nameA = a.name || '';
    const nameB = b.name || '';
    return nameA.localeCompare(nameB);
  });

  // When editing a subtask whose resource_id doesn't match any assignment in
  // the current dropdown list, surface the previously assigned resource as a
  // synthetic option so the dropdown isn't blank.
  const hasMatchingAssignment = !!sortedResourceAssignments.find(
    (a) => a.id === formData.resource_id
  );
  const showSyntheticCurrent =
    !!editingSubtask &&
    !!formData.resource_id &&
    !hasMatchingAssignment;
  const syntheticLabel =
    (editingSubtask?.name || 'Current resource') +
    (editingSubtask?.role ? ` - ${editingSubtask.role}` : '');

  // Prepare step options
  const stepOptions = existingSteps.length > 0 ? existingSteps : [{ step: 1, step_name: 'Step 1' }];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-white text-black max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editingSubtask ? 'Edit Subtask' : 'Create New Subtask'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-2">
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label htmlFor="subtask_name">Subtask Name *</Label>
              {isDuplicateName && (
                <span className="text-red-500 text-sm">
                  Duplicate name exists. Choose a new subtask name
                </span>
              )}
            </div>
            <Input
              id="subtask_name"
              value={formData.subtask_name}
              onChange={handleSubtaskNameChange}
              placeholder="Enter subtask name"
              required
              className={isDuplicateName ? 'border-red-500 bg-white text-black' : 'bg-white text-black'}
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              className="bg-white text-black"
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Enter subtask description (optional)"
              rows={2}
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="resource_id">Assigned Resource *</Label>
            <Select
              value={formData.resource_id}
              onValueChange={handleResourceChange}
            >
              <SelectTrigger className="bg-white text-black" >
                <SelectValue className="bg-white text-black" placeholder="Select a resource" />
              </SelectTrigger>
              <SelectContent className="bg-white text-black" >
                {showSyntheticCurrent && (
                  <SelectItem key={`current-${formData.resource_id}`} value={formData.resource_id}>
                    {syntheticLabel}
                  </SelectItem>
                )}
                {sortedResourceAssignments.map((assignment) => (
                  <SelectItem key={assignment.id} value={assignment.id}>
                    {assignment.name} - {assignment.role}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="step">Step</Label>
              <Select
                value={formData.step.toString()}
                onValueChange={handleStepChange}
              >
                <SelectTrigger className="bg-white text-black" >
                  <SelectValue className="bg-white text-black" />
                </SelectTrigger>
                <SelectContent>
                  {stepOptions.map((stepOption) => (
                    <SelectItem className="bg-white text-black" key={stepOption.step} value={stepOption.step.toString()}>
                      {stepOption.step_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label htmlFor="status">Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value: 'To Do' | 'In Progress' | 'Done') => 
                  setFormData(prev => ({ ...prev, status: value }))
                }
              >
                <SelectTrigger className="bg-white text-black" >
                  <SelectValue className="bg-white text-black" />
                </SelectTrigger>
                <SelectContent className="bg-white text-black">
                  <SelectItem value="To Do">To Do</SelectItem>
                  <SelectItem value="In Progress">In Progress</SelectItem>
                  <SelectItem value="Done">Done</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor="email">Resource Email</Label>
            <Input
              id="email"
              type="email"
              className="bg-white text-black"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              placeholder="Auto-populated when resource is selected"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="estimated_hours">Estimated Hours (per day)</Label>
              <Input
                className="bg-white text-black"
                id="estimated_hours"
                type="number"
                min="0.5"
                step="0.5"
                value={formData.estimated_hours}
                onChange={(e) => setFormData(prev => ({ ...prev, estimated_hours: parseFloat(e.target.value) || 8 }))}
              />
            </div>

            {formData.status !== 'To Do' && (
              <div className="space-y-1">
                <Label htmlFor="actual_hours">Actual Hours (per day)</Label>
                <Input
                  className="bg-white text-black"
                  id="actual_hours"
                  type="number"
                  min="0"
                  step="0.5"
                  value={formData.actual_hours || ''}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    actual_hours: e.target.value ? parseFloat(e.target.value) : undefined 
                  }))}
                  placeholder="Enter actual hours worked"
                />
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="start_date">Start Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    id="start_date"
                    type="button"
                    variant="outline"
                    className="w-full justify-start text-left font-normal bg-white text-black border-gray-300"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {displayDate(formData.start_date)}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-white" align="start">
                  <Calendar
                    mode="single"
                    weekStartsOn={0}
                    selected={parseYMD(formData.start_date) || undefined}
                    onSelect={(date) => {
                      if (date) setFormData(prev => ({ ...prev, start_date: toYMDString(date) }));
                    }}
                    initialFocus
                    className="p-3 pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-1">
              <Label htmlFor="end_date">End Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    id="end_date"
                    type="button"
                    variant="outline"
                    className="w-full justify-start text-left font-normal bg-white text-black border-gray-300"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {displayDate(formData.end_date)}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-white" align="start">
                  <Calendar
                    mode="single"
                    weekStartsOn={0}
                    selected={parseYMD(formData.end_date) || undefined}
                    onSelect={(date) => {
                      if (date) setFormData(prev => ({ ...prev, end_date: toYMDString(date) }));
                    }}
                    initialFocus
                    className="p-3 pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <DialogFooter className="pt-3">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="bg-white text-black border-gray-300"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading || !formData.subtask_name.trim() || (!formData.resource_id && !editingSubtask) || isDuplicateName}
              className="bg-primary text-primary-foreground"
            >
              {isLoading ? 'Saving...' : editingSubtask ? 'Update Subtask' : 'Create Subtask'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SubtaskForm;
